import CalendarDay from '@app/state/calendar/CalendarDay';
import React, { useMemo } from 'react';
import MarketRegionState from '@app/model/market_area/MarketAreaState';
import MarketRegionStateComparisonOutput from '@app/model/market_area/MarketRegionStateComparisonOutput';
import MarketRegionStateCluster from '@app/model/market_area/MarketRegionStateCluster';
import { ArrowDownIcon, ArrowUpIcon, FireIcon } from '@heroicons/react/20/solid';
import SnowFlakeIcon from '@images/icons/snowflake-solid.svg';
import Utilities from '@util/Utilities';
import { useQuery } from '@tanstack/react-query';
import MarketInsightService from '@app/model/market_insight/service/MarketInsightService';
import { MarketInsightQueryKeys } from '@app/model/market_insight/service/MarketInsightQueryKeys';
import { averageSoldOutRate } from './MarketStateSnapshotDailyMarketInsightFragment.utilities';
import { ArrowTrendingDownIcon, ArrowTrendingUpIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import _isEmpty from 'lodash/isEmpty';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import { RangeSelector } from '@util/RangeSelector';
import { Tooltip } from 'react-tooltip';
import { useMarketPaceQuery } from '@app/model/market_insight/service/MarketPaceServiceHooks';
import { PaceChip } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market_insight/fragment/market_pace/PaceChip';
import {
	PACE_ERROR_LABEL,
	PACE_VERDICT_LABEL,
	PACE_VERDICT_SHORT,
	explanationLines,
	methodLines,
	reasonText,
	runLabel,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market_insight/fragment/market_pace/MarketPaceText';

const MarketStateSnapshotDailyMarketInsightFragment: React.FunctionComponent<{
	calendarDay: CalendarDay,
	marketAreaStateComparison: MarketRegionStateComparisonOutput
}> = ({ calendarDay, marketAreaStateComparison }) => {


	const renderIcon = (marketAreaState: MarketRegionState) => {
		if (marketAreaState.cluster === MarketRegionStateCluster.VERY_HIGH)
			return <FireIcon className="w-5 h-5 text-red-500" />;
		else if (marketAreaState.cluster === MarketRegionStateCluster.VERY_LOW)
			return <SnowFlakeIcon className="w-5 h-5 fill-blue-500" />;
		else return null;
	};

	const renderTrend = useMemo(() => {
		const difference = (marketAreaStateComparison.current.demand - (marketAreaStateComparison.previous != null ? marketAreaStateComparison.previous.demand : 0)) / marketAreaStateComparison.current.demand;
		if (difference == 0) return <div></div>;
		if (difference > 0) return <div
			className="inline-flex items-center rounded-full bg-green-100 px-1 py-1 text-sm font-medium text-green-700">
			<ArrowUpIcon className="w-3 h-3 shrink-0 self-center" />
		</div>;
		return <div className="inline-flex items-center rounded-full bg-red-100 px-1 py-1 text-sm font-medium text-red-700">
			<ArrowDownIcon className="w-3 h-3 shrink-0 self-center" />
		</div>;
	}, [marketAreaStateComparison]);

	const { companyHotel } = useSelectedCompanyHotel();
	// The pace: only with the pace setting of the hotel, and only for a night that is not past.
	const showPace = companyHotel.settings.main.enableMarketPace && !calendarDay.isPast;

	return <div className="mt-4">
		<div className="rounded-md bg-blue-50 shadow ring-1 ring-black ring-opacity-5 p-2">
			<div className="md:flex items-center justify-between gap-x-2">
				<div className="px-3 py-2 rounded-md">
					<div className="flex items-center gap-x-2">
						<div className="flex flex-wrap items-center gap-x-1">
							<div
								className="shrink-0 text-base leading-tight font-bold text-gray-700 max-w-[265px] truncate">{marketAreaStateComparison.marketArea.name}</div>
							<div className="text-base leading-tight font-light text-gray-600">Demand</div>
						</div>
						<div className="flex items-center gap-x-0.5 shrink-0">
							{renderIcon(marketAreaStateComparison.current)}
							<p
								className="text-lg font-semibold text-gray-900">{Utilities.formatDouble(marketAreaStateComparison.current.demand, 0)}%</p>
						</div>
						{renderTrend}
					</div>
				</div>
				<div className="sm:flex items-center gap-x-2 shrink-0 space-y-2 sm:space-y-0">
					<RenderOccupancyIndex statDate={calendarDay.display} />
					<RenderSoldOutIndex statDate={calendarDay.display} />
					<RenderPriceIndex statDate={calendarDay.display} />
				</div>
			</div>
			<RenderTopMarkets statDate={calendarDay.display} showPace={showPace} />
		</div>
	</div>;
};

const RenderLoadingIndex = React.memo(() => {
	return <div className="px-3 py-2 bg-white rounded-md ring-1 ring-inset ring-gray-700/10">
		<div className="flex items-center h-full">
			<div className="w-full animate-pulse">
				<div className="h-2 bg-gray-200 rounded-full mb-1"></div>
				<div className="h-2 bg-gray-200 rounded-full"></div>
			</div>
		</div>
	</div>;
});

const RenderEmptyIndex = React.memo(() => {
	return <div className="px-3 py-2 bg-white rounded-md ring-1 ring-inset ring-gray-700/10">
		<div className="flex items-center gap-x-1.5">
			<p className="mt-0.5 text-xs font-normal text-gray-400">No data found!</p>
		</div>
		<div className="overflow-hidden rounded-full w-full bg-gray-200/70">
			<div className="h-1.5 rounded-full bg-gray-300 w-[70%]"></div>
		</div>
	</div>;
});

const RenderOccupancyIndex: React.FunctionComponent<{ statDate: string }> = ({ statDate }) => {
	const { companyHotel } = useSelectedCompanyHotel();

	const { data: occupancyIndices, isLoading, isSuccess } = useQuery({
		queryFn: () => MarketInsightService.findOccupancyIndices({ companyHotelId: companyHotel.id, statDate: statDate }),
		queryKey: MarketInsightQueryKeys.occupancyIndices(companyHotel.id, statDate),
	});

	if (isLoading) {
		return <RenderLoadingIndex />;
	}
	if (isSuccess && occupancyIndices?.marketOccupancyRate != null) {

		const rangeSelector = RangeSelector.create([
			{ min: 0, max: 50, value: 'text-green-600' },
			{ min: 50, max: 74, value: 'text-blue-600' },
			{ min: 75, max: 100, value: 'text-red-600' },
		]);

		const index = occupancyIndices.marketOccupancyRate;
		return <>
			<div data-tooltip-id="occupancy-tooltip"
						data-tooltip-html={`The percentage of total market inventory<br/> currently booked for this date.`}>
				<RenderIndex index={index * 100} title={<>
					<span>Market</span>
					<span className="block">Occupancy</span>
				</>} rangeSelector={rangeSelector} />
			</div>
			<Tooltip id="occupancy-tooltip" />
		</>;
	}
	return <RenderEmptyIndex />;
};

const RenderSoldOutIndex: React.FunctionComponent<{ statDate: string }> = ({ statDate }) => {
	const { companyHotel } = useSelectedCompanyHotel();

	const { data: soldOutIndices, isLoading, isSuccess } = useQuery({
		queryFn: () => MarketInsightService.findSoldOutIndices({ companyHotelId: companyHotel.id, statDate: statDate }),
		queryKey: MarketInsightQueryKeys.soldOutIndices(companyHotel.id, statDate),
	});

	if (isLoading) {
		return <RenderLoadingIndex />;
	}
	if (isSuccess && soldOutIndices != null) {

		const rangeSelector = RangeSelector.create([
			{ min: 0, max: 50, value: 'text-green-600' },
			{ min: 50, max: 74, value: 'text-blue-600' },
			{ min: 75, max: 100, value: 'text-red-600' },
		]);

		const index = averageSoldOutRate(soldOutIndices.indices);
		if (index == null) return <RenderEmptyIndex />;

		return <>
			<div data-tooltip-id="sold-out-tooltip"
						data-tooltip-html={`The percentage of market properties<br/> with no availability.`}>
				<RenderIndex index={index * 100} title="Sold Out" rangeSelector={rangeSelector} />
			</div>
			<Tooltip id="sold-out-tooltip" />
		</>;
	}
	return <RenderEmptyIndex />;
};

const RenderPriceIndex: React.FunctionComponent<{ statDate: string }> = ({ statDate }) => {
	const { companyHotel } = useSelectedCompanyHotel();

	const { data: priceIndex, isLoading, isSuccess } = useQuery({
		queryFn: () => MarketInsightService.findPriceIndex({ companyHotelId: companyHotel.id, statDate: statDate }),
		queryKey: MarketInsightQueryKeys.priceIndex(companyHotel.id, statDate),
	});

	if (isLoading) {
		return <RenderLoadingIndex />;
	}
	if (isSuccess && priceIndex != null && priceIndex.marketPriceMedianDifference != null) {

		const rangeSelector = RangeSelector.create([
			{ min: -100, max: -1, value: 'text-green-600' },
			{ min: 0, max: 100, value: 'text-red-600' },
		]);

		const index = priceIndex.marketPriceMedianDifference;
		return <>
			<div data-tooltip-id="price-index-tooltip"
						data-tooltip-html={`Shows how much market median prices have risen<br/> or dropped compared to ${priceIndex.periodInDays} days ago.`}>
				<RenderIndex index={index * 100} title={<>
					<span>Price Index</span>
					<span className="block text-[9px] font-light text-gray-400 ">vs. {priceIndex.periodInDays} days ago</span>
				</>} rangeSelector={rangeSelector} showSign />
			</div>
			<Tooltip id="price-index-tooltip" />
		</>;
	}
	return <RenderEmptyIndex />;
};

// The pace of the hotel against its market for this night (ROO2-171 verdict, findMarketPace for one date), at the
// right end of the Top 3 Origins line. The tag is the PaceChip and the short verdict, in the look of an origin tag.
// The hover gives the full verdict, the sentences of the night (or the reason of no comparison), the source line of
// the method and, for an older run, its date.
const RenderPaceTag: React.FunctionComponent<{ statDate: string }> = ({ statDate }) => {
	const { companyHotel } = useSelectedCompanyHotel();
	const { data: pace, isLoading, isError } = useMarketPaceQuery(companyHotel.id, statDate, statDate, true);
	const label = <p className="text-xs font-medium text-gray-500 whitespace-nowrap">Your pace vs market:</p>;

	if (isLoading) {
		return <div className="flex items-center gap-x-1.5">{label}<div className="h-2 w-16 rounded-full bg-gray-200 animate-pulse" /></div>;
	}
	const day = pace?.days.find(paceDay => paceDay.statDate === statDate);
	if (isError || !pace || !day) {
		return <div className="flex items-center gap-x-1.5">{label}<p className="text-xs text-gray-400 whitespace-nowrap">{PACE_ERROR_LABEL}</p></div>;
	}

	const lines = day.verdict === 'NO_COMPARISON' ? [reasonText(day)].filter((line): line is string => line !== null) : explanationLines(day);
	const footer = [methodLines(pace.ownSource)[0]];
	if (pace.run !== null && !pace.run.isLatest) footer.push(runLabel(pace.run));
	const html = `<b>${PACE_VERDICT_LABEL[day.verdict]}</b><br/>${lines.join('<br/>')}`
		+ `<div class="mt-2 text-xs text-gray-300">${footer.join('<br/>')}</div>`;

	return <>
		<div className="flex items-center gap-x-1.5 cursor-pointer" data-tooltip-id="pace-tooltip" data-tooltip-html={html}>
			{label}
			<div className="inline-flex items-center gap-x-1 rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-500/10 whitespace-nowrap">
				<PaceChip verdict={day.verdict} />
				<span>{PACE_VERDICT_SHORT[day.verdict]}</span>
			</div>
			<span className="text-base font-serif italic text-gray-300 ml-0.5">i</span>
		</div>
		{/* Below the tag. The card is inside the day popup (itself a tooltip): a fixed position measures the window, not
		    the popup, and z-50 keeps the hover above the hotel table. */}
		<Tooltip id="pace-tooltip" place="bottom" positionStrategy="fixed" className="max-w-xs z-50" />
	</>;
};

const RenderIndex: React.FunctionComponent<{
	index: number,
	title: React.ReactNode,
	rangeSelector: RangeSelector<string>,
	showSign?: boolean
}> = ({ index, title, rangeSelector, showSign = false }) => {
	const rounded = Math.round(index);

	return <div className="px-3 py-2 h-10 bg-white rounded-md ring-1 ring-inset ring-gray-700/10 cursor-pointer">
		<div className="flex items-center h-full gap-x-1">
			<p className="text-xs font-normal text-gray-400 leading-none">{title}</p>
			<div className="h-4 w-px bg-gray-300 mx-1 block" />
			<p className={`text-base font-bold leading-none tracking-tight ${rangeSelector.getValue(rounded)}`}>
				{showSign ? (index > 0 ? '+' : (index < 0 ? '-' : '')) : ''}{Math.abs(rounded)}%
			</p>
			<span className="text-base font-serif italic text-gray-300 ml-0.5">i</span>
		</div>
	</div>;
};

const RenderTopMarkets: React.FunctionComponent<{ statDate: string, showPace: boolean }> = ({ statDate, showPace }) => {

	const { companyHotel } = useSelectedCompanyHotel();

	const { data: flightStats, isSuccess } = useQuery({
		queryFn: () => MarketInsightService.findFlightStats({
			companyHotelId: companyHotel.id,
			statDate: statDate,
			beforeDays: 14,
		}),
		queryKey: MarketInsightQueryKeys.flightStats(companyHotel.id, statDate),
	});
	const hasOrigins = isSuccess && flightStats != null && !_isEmpty(flightStats);
	// The line shows for the origins, for the pace, or for both. The pace goes to the right end of the line.
	if (!hasOrigins && !showPace) return <div />;

	return <div className="md:flex items-center gap-x-3 border-t border-gray-200 mt-2 pt-2 px-2.5">
		{hasOrigins && <>
			<div className="flex items-center gap-x-1">
				<GlobeAltIcon className="w-4 h-4 text-gray-400" />
				<p className="text-xs font-medium text-gray-500">Top 3 Origins:</p>
			</div>
			{flightStats.slice(0, 3).map(stat => <div key={stat.origin}
																								className="inline-flex items-center gap-x-1 rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-500/10">
				<span>{Utilities.snakeCaseToWords(stat.origin)}</span>
				{stat.currentValue > stat.previousValue &&
					<ArrowTrendingUpIcon className="w-3 h-3 shrink-0 self-center text-green-600" />}
				{stat.currentValue < stat.previousValue &&
					<ArrowTrendingDownIcon className="w-3 h-3 shrink-0 self-center text-red-600" />}
			</div>)}
		</>}
		{showPace && <div className={`md:ml-auto ${hasOrigins ? 'mt-2 md:mt-0' : ''}`}><RenderPaceTag statDate={statDate} /></div>}
	</div>;
};

export default MarketStateSnapshotDailyMarketInsightFragment;